import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldAlert, KeyRound, QrCode, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setEnableCode("");
      setRecoveryCodes(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    if (!/^\d{6}$/.test(enableCode.trim())) { toast.error("Enter the 6-digit code from your authenticator app."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { token: enableCode.trim() });
      setRecoveryCodes(data.recovery_codes);
      setSetupData(null);
      toast.success("2FA enabled · save your recovery codes!");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!disableCode.trim()) { toast.error("Enter a 6-digit code (or recovery code) to confirm."); return; }
    if (!window.confirm("Disable 2FA? Your account will be protected by password only.")) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { token: disableCode.trim() });
      toast.success("2FA disabled.");
      setDisableCode("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  if (!status) return <Card className="p-6 text-sm text-ink-muted">Loading…</Card>;
  if (!status.allowed_for_role) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3"><ShieldAlert className="text-amber-500" size={20} />
          <div>
            <div className="font-semibold">2FA not required for your role</div>
            <div className="text-xs text-ink-muted">Two-factor authentication is currently mandatory only for admin roles (Super Admin · Hospital Admin · Branch Admin · IT Admin).</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status pill */}
      <Card className="flex items-center justify-between p-5">
        <div className="flex items-center gap-3">
          {status.enabled
            ? <ShieldCheck className="text-emerald-600" size={28} />
            : <ShieldAlert className="text-amber-500" size={28} />}
          <div>
            <div className="font-heading text-lg">{status.enabled ? "Two-Factor Authentication enabled" : "Two-Factor Authentication disabled"}</div>
            <div className="text-xs text-ink-muted">
              {status.enabled
                ? `Recovery codes remaining: ${status.recovery_codes_remaining}/8 · Login requires a code from your authenticator app.`
                : "Add an extra layer of security. We strongly recommend this for admin accounts."}
            </div>
          </div>
        </div>
        {!status.enabled && !setupData && (
          <Button onClick={startSetup} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="2fa-start-setup-btn">
            <ShieldCheck size={14} className="mr-2" />Enable 2FA
          </Button>
        )}
      </Card>

      {/* Setup wizard */}
      {setupData && (
        <Card className="p-5 space-y-4" data-testid="2fa-setup-card">
          <div className="flex items-center gap-2 text-brand">
            <QrCode size={18} /><div className="font-heading text-base">Step 1 of 2 · Scan with your authenticator</div>
          </div>
          <div className="grid items-center gap-6 md:grid-cols-[200px_1fr]">
            <div className="rounded-lg border-2 border-border bg-white p-3 inline-block">
              <img src={setupData.qr_data_uri} alt="2FA QR code" className="h-44 w-44" data-testid="2fa-qr-img" />
            </div>
            <div className="space-y-2 text-sm">
              <div>Open <b>Google Authenticator</b> / <b>Microsoft Authenticator</b> / <b>Authy</b> and scan the QR code above.</div>
              <div className="text-xs text-ink-muted">Can't scan? Enter this key manually instead:</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-secondary px-3 py-2 text-xs font-mono" data-testid="2fa-secret">{setupData.secret_b32}</code>
                <Button size="sm" variant="outline" onClick={() => copy(setupData.secret_b32)}><Copy size={13} /></Button>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-2 text-brand mb-2">
              <KeyRound size={18} /><div className="font-heading text-base">Step 2 of 2 · Verify a code</div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <Label>6-digit code from authenticator app</Label>
                <Input value={enableCode} onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456" inputMode="numeric" className="font-mono text-lg tracking-widest" data-testid="2fa-enable-code-input" />
              </div>
              <Button onClick={confirmEnable} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="2fa-enable-confirm-btn">
                <CheckCircle2 size={14} className="mr-2" />Verify & enable
              </Button>
              <Button variant="outline" onClick={() => setSetupData(null)} disabled={busy}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Recovery codes — shown once after enabling */}
      {recoveryCodes && (
        <Card className="border-2 border-amber-300 bg-amber-50/60 p-5" data-testid="2fa-recovery-codes-card">
          <div className="flex items-center gap-2 text-amber-700 font-heading text-base mb-2">
            <AlertTriangle size={18} />Save these recovery codes now — they will not be shown again
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {recoveryCodes.map((c, i) => (
              <code key={i} className="rounded bg-white px-3 py-2 text-xs font-mono border border-amber-200" data-testid={`2fa-recovery-${i}`}>{c}</code>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs">
            <div className="text-ink-muted">Each code can be used <b>once</b> to login if you lose your phone. Store them in a password manager.</div>
            <Button size="sm" variant="outline" onClick={() => copy(recoveryCodes.join("\n"))}><Copy size={12} className="mr-1" />Copy all</Button>
          </div>
        </Card>
      )}

      {/* Disable section */}
      {status.enabled && (
        <Card className="p-5" data-testid="2fa-disable-card">
          <div className="font-heading text-base mb-1">Disable 2FA</div>
          <div className="text-xs text-ink-muted mb-3">Enter a code from your authenticator (or a recovery code) to confirm.</div>
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label>Code</Label>
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="6-digit or recovery code" data-testid="2fa-disable-code-input" />
            </div>
            <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={disable} disabled={busy} data-testid="2fa-disable-confirm-btn">
              Disable 2FA
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
